import { useMutation } from "react-query";
import axios, { AxiosError } from "axios";
import { FormikHelpers } from "formik";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "i18n";
import { IGraduatesApplication } from "../types";
import useSetGradsApplicationData from "./useSetGradsApplicationData";
import { config } from "../../../../config/common";
import { checkForRecurlyPostCodeError } from "../../../../utils/checkForRecurlyPostCodeError";
import { gradsFormNavigationMap, GraduatesEndpoints } from "../constants";

function useSubmitGradsApplicationForm<T>(
  onlineApplicationId: string | number | undefined,
  securityKey: string | undefined,
  errorCallback: () => void,
  endpoint: GraduatesEndpoints
) {
  const setGradsApplicationData = useSetGradsApplicationData();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { mutateAsync, error } = useMutation<
    { data: IGraduatesApplication },
    AxiosError,
    T
  >({
    mutationFn: (data) => {
      return axios.post(
        `${config.userAccountApiUrl}/application/graduate/${endpoint}`,
        data,
        { params: { onlineApplicationId, securityKey } }
      );
    },
  });

  const submitForm = async (values: T, formikHelpers?: FormikHelpers<T>) => {
    try {
      const result = await mutateAsync(values);
      setGradsApplicationData(result.data);
      navigate(gradsFormNavigationMap[endpoint]);
    } catch (error) {
      const axiosError = error as AxiosError;

      if (formikHelpers) {
        const hasPostCodeValidation = checkForRecurlyPostCodeError(
          { cause: axiosError?.response?.data },
          formikHelpers?.setFieldError,
          t
        );

        if (!hasPostCodeValidation) {
          errorCallback();
        }
      }
    }
  };

  return {
    submitForm,
    error,
  };
}

export default useSubmitGradsApplicationForm;
